/**
 * Import & Connect Tab
 * ─────────────────────
 * Section A: CSV Import wizard (5 entity types)
 *   Step 1 → Choose entity
 *   Step 2 → Upload CSV, auto-detect columns
 *   Step 3 → Map fields
 *   Step 4 → Preview (first 5 rows)
 *   Step 5 → Import + results
 *
 * Section B: API Connectors (Shopify, WooCommerce, + framework)
 */
import React, { useState, useRef } from 'react';
import axios from 'axios';
import {
  Upload, Table, CheckCircle, AlertTriangle, X, Download,
  Plug, RefreshCw, ChevronRight, ChevronLeft, ChevronDown, Play,
  Package, Users, Truck, ShoppingCart, Archive, ArrowDownFromLine, ArrowUpFromLine,
  Settings2, History, Bell
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api/wms';

// ─── Entity config ────────────────────────────────────────────────────────────

const ENTITIES = {
  products: {
    label: 'Products',
    icon: Package,
    color: 'blue',
    required: ['name'],
    fields: [
      { key: 'name',         label: 'Product Name *', required: true },
      { key: 'sku',          label: 'SKU' },
      { key: 'brand',        label: 'Brand' },
      { key: 'category',     label: 'Category' },
      { key: 'price',        label: 'Selling Price' },
      { key: 'cost',         label: 'Cost Price' },
      { key: 'stock',        label: 'Stock Qty' },
      { key: 'min_stock',    label: 'Min Stock Alert' },
      { key: 'weight',       label: 'Weight (kg)' },
      { key: 'warehouse_id', label: 'Warehouse ID' },
    ],
  },
  customers: {
    label: 'Customers',
    icon: Users,
    color: 'purple',
    required: ['name'],
    fields: [
      { key: 'name',        label: 'Customer Name *', required: true },
      { key: 'email',       label: 'Email' },
      { key: 'phone',       label: 'Phone' },
      { key: 'address',     label: 'Address' },
      { key: 'city',        label: 'City' },
      { key: 'postal_code', label: 'Postal Code' },
    ],
  },
  suppliers: {
    label: 'Suppliers',
    icon: Truck,
    color: 'emerald',
    required: ['name'],
    fields: [
      { key: 'name',           label: 'Contact Name *', required: true },
      { key: 'company',        label: 'Company' },
      { key: 'email',          label: 'Email' },
      { key: 'phone',          label: 'Phone' },
      { key: 'address',        label: 'Address' },
      { key: 'city',           label: 'City' },
      { key: 'country',        label: 'Country' },
      { key: 'payment_terms',  label: 'Payment Terms' },
      { key: 'lead_time_days', label: 'Lead Time (days)' },
    ],
  },
  orders: {
    label: 'Orders',
    icon: ShoppingCart,
    color: 'orange',
    required: ['customer_name'],
    fields: [
      { key: 'order_id',       label: 'Order ID' },
      { key: 'customer_name',  label: 'Customer Name *', required: true },
      { key: 'customer_email', label: 'Customer Email' },
      { key: 'marketplace',    label: 'Marketplace' },
      { key: 'product_name',   label: 'Product Name' },
      { key: 'sku',            label: 'SKU' },
      { key: 'quantity',       label: 'Quantity' },
      { key: 'unit_price',     label: 'Unit Price' },
      { key: 'shipping_fee',   label: 'Shipping Fee' },
      { key: 'discount',       label: 'Discount' },
      { key: 'status',         label: 'Status' },
      { key: 'created_at',     label: 'Date' },
    ],
  },
  purchases: {
    label: 'Purchase History',
    icon: Archive,
    color: 'teal',
    required: ['product_name', 'quantity', 'unit_cost'],
    fields: [
      { key: 'product_name', label: 'Product Name *', required: true },
      { key: 'sku',          label: 'SKU' },
      { key: 'quantity',     label: 'Quantity *', required: true },
      { key: 'unit_cost',    label: 'Unit Cost *', required: true },
      { key: 'supplier',     label: 'Supplier' },
      { key: 'warehouse_id', label: 'Warehouse ID' },
      { key: 'reference',    label: 'Invoice Ref' },
      { key: 'date',         label: 'Date' },
    ],
  },
};

// ─── CSV parser ───────────────────────────────────────────────────────────────

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  // Detect delimiter
  const firstLine = lines[0];
  const delim = [',', '\t', ';'].sort((a, b) =>
    (firstLine.split(b).length - firstLine.split(a).length))[0];
  const splitLine = (line) => {
    const result = []; let cur = ''; let inQ = false;
    for (const ch of line) {
      if (ch === '"') { inQ = !inQ; }
      else if (ch === delim && !inQ) { result.push(cur.trim()); cur = ''; }
      else { cur += ch; }
    }
    result.push(cur.trim());
    return result;
  };
  const headers = splitLine(lines[0]);
  const rows = lines.slice(1).map(line => {
    const vals = splitLine(line);
    return Object.fromEntries(headers.map((h, i) => [h, vals[i] || '']));
  });
  return { headers, rows };
}

// ─── API Connector card ───────────────────────────────────────────────────────

function ConnectorCard({ platform, label, logo, fields, defaultOpen = false, onToggleOpen }) {
  const [form, setForm]         = useState({});
  const [testing, setTesting]   = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syncing, setSyncing]   = useState({});
  const [syncRes, setSyncRes]   = useState({});
  const [open, setOpen]         = useState(defaultOpen);
  const [schedule, setSchedule] = useState({ enabled: false, entities: ['orders'] });
  const [savedInfo, setSavedInfo] = useState(null);
  const [scheduleMsg, setScheduleMsg] = useState('');
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  // Load status on mount (for collapsed header display)
  React.useEffect(() => {
    axios.get(`${API}/connectors/list`).then(r => {
      const conn = r.data.find(c => c.platform === platform);
      if (conn) setSavedInfo(conn);
    }).catch(() => {});
  }, [platform]);  // run once per platform

  // Full load when card is opened (pre-fills form, handles bad tokens, etc.)
  React.useEffect(() => {
    if (!open) return;
    axios.get(`${API}/connectors/list`).then(r => {
      const conn = r.data.find(c => c.platform === platform);
      if (!conn) return;

      // Auto-delete ONLY genuinely bad tokens (short AND not masked by the API).
      // The list endpoint returns masked tokens like "shpa********" — don't delete those!
      const badTokenField = fields.find(f => {
        const val = conn[f.key];
        return f.secret && val && !val.includes('*') && val.length < 20;
      });
      if (badTokenField) {
        axios.delete(`${API}/connectors/${platform}`).catch(() => {});
        setSavedInfo(null); setTestResult(null);
        setForm({ shop_url: conn.shop_url || conn.site_url || '' });
        return;
      }

      setSavedInfo(conn);
      setSchedule({ enabled: conn.scheduled_sync || false, entities: conn.sync_entities || ['orders'] });

      // Pre-populate fields. Show masked tokens as dots in the password field.
      // The backend recognizes masked tokens (containing *) and uses the stored real token instead.
      const { platform: _p, updated_at, scheduled_sync, sync_entities,
              last_synced, last_sync_status, ...creds } = conn;
      setForm(creds);

      if (conn.last_synced && conn.last_sync_status === 'ok') {
        setTestResult({ ok: true, msg: `Last synced: ${new Date(conn.last_synced).toLocaleString()}` });
      }
    }).catch(() => {});
  }, [open, platform]);

  // Compute "next run at HH:30"
  const nextRunTime = () => {
    const now = new Date();
    const next = new Date(now);
    if (now.getMinutes() >= 30) {
      next.setHours(now.getHours() + 1);
    }
    next.setMinutes(30);
    next.setSeconds(0);
    return next.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const test = async () => {
    setTesting(true); setTestResult(null);

    // Build API payload separately — don't wipe the form state
    const apiPayload = { ...form };

    // Clean shop URL — update BOTH the form display and the payload
    if (apiPayload.shop_url) {
      const cleanUrl = apiPayload.shop_url.trim().replace(/^https?:\/\//i, '').replace(/\/$/, '').toLowerCase();
      apiPayload.shop_url = cleanUrl;
      setForm(p => ({ ...p, shop_url: cleanUrl }));  // only update URL, leave token intact
    }

    // For secret fields: if masked (*) → remove from payload (backend uses stored token).
    // If real new token → sanitize. Either way, DON'T touch the form state.
    const fields_with_secrets = fields.filter(f => f.secret);
    fields_with_secrets.forEach(f => {
      const val = apiPayload[f.key];
      if (!val) return;
      if (val.includes('*')) {
        delete apiPayload[f.key];   // backend falls back to stored real token
      } else {
        apiPayload[f.key] = val.replace(/[^\x20-\x7E]/g, '').trim().replace(/^["'`\s]+|["'`\s]+$/g, '');
      }
    });

    try {
      const r = await axios.post(`${API}/connectors/${platform}/test`, apiPayload);
      setTestResult({ ok: true, msg: `Connected to "${r.data.shop_name || r.data.site || '✓'}"` });
      // Only save if a NEW real token was provided (not masked)
      const hasNewToken = fields_with_secrets.some(f => apiPayload[f.key] && apiPayload[f.key].length > 10);
      if (hasNewToken) {
        await axios.post(`${API}/connectors/save`, { platform, ...apiPayload });
        setSavedInfo(p => ({ ...p, ...apiPayload }));
      }
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      const status = e.response?.status;
      let msg = 'Connection failed';
      if (status === 401 || detail.includes('invalid access token') || detail.includes('Invalid Admin')) {
        msg = 'Invalid token — copy the full shpat_xxx token from Shopify → Reinstall app → Reveal token';
      } else if (status === 403 || detail.includes('Forbidden')) {
        msg = 'Access denied (403) — remove store password protection OR add scopes: read_products, read_orders, read_customers';
      } else if (detail.includes('connect') || e.code === 'ECONNREFUSED') {
        msg = 'Cannot reach Shopify — check Shop URL';
      } else if (detail) {
        msg = detail;
      }
      setTestResult({ ok: false, msg });
    }
    setTesting(false);
  };

  const sync = async (entity) => {
    setSyncing(p => ({ ...p, [entity]: true }));
    try {
      const r = await axios.post(`${API}/connectors/${platform}/sync/${entity}`, form);
      setSyncRes(p => ({ ...p, [entity]: r.data }));
    } catch (e) {
      setSyncRes(p => ({ ...p, [entity]: { error: e.response?.data?.detail || 'Sync failed' } }));
    }
    setSyncing(p => ({ ...p, [entity]: false }));
  };

  const saveSchedule = async () => {
    setScheduleSaving(true);
    setScheduleMsg('');
    try {
      await axios.put(`${API}/connectors/${platform}/schedule`, {
        enabled:    schedule.enabled,
        entities:   schedule.entities,
        interval_h: 1,
      });
      // Update savedInfo to reflect new state
      setSavedInfo(p => ({ ...p, scheduled_sync: schedule.enabled, sync_entities: schedule.entities }));
      setScheduleMsg(schedule.enabled
        ? `✓ Auto-sync ON — next run at ${nextRunTime()} · syncing: ${schedule.entities.join(', ')}`
        : '✓ Schedule disabled — auto-sync will not run');
    } catch (e) {
      setScheduleMsg('Save failed: ' + (e.response?.data?.detail || 'Check backend connection'));
    }
    setScheduleSaving(false);
    setTimeout(() => setScheduleMsg(''), 6000);
  };

  const triggerNow = async () => {
    setRunningNow(true);
    try {
      const r = await axios.post(`${API}/connectors/sync-all-orders`);
      const results = r.data.synced || {};
      const msg = Object.entries(results).map(([p, res]) =>
        res.error ? `${p}: ${res.error}` : `${p}: ${res.imported || 0} new orders`
      ).join(' · ');
      setScheduleMsg(`✓ Manual sync complete — ${msg || 'no new orders'}`);
    } catch (e) {
      setScheduleMsg('Trigger failed: ' + (e.response?.data?.detail || 'Error'));
    }
    setRunningNow(false);
    setTimeout(() => setScheduleMsg(''), 6000);
  };

  const toggleEntity = (e) => {
    setSchedule(p => ({
      ...p,
      entities: p.entities.includes(e) ? p.entities.filter(x => x !== e) : [...p.entities, e]
    }));
  };

  const isConnected = testResult?.ok || !!savedInfo;

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
      <button onClick={() => {
      const next = !open;
      setOpen(next);
      if (onToggleOpen) onToggleOpen(next);
    }} className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{logo}</span>
          <div className="text-left">
            <p className="font-semibold">{label}</p>
            {savedInfo ? (
              <p className="text-xs text-emerald-600">
                {savedInfo.scheduled_sync ? '🔄 Auto-sync ON · ' : ''}
                {savedInfo.last_synced
                  ? `Last synced: ${new Date(savedInfo.last_synced).toLocaleString()}`
                  : savedInfo.shop_url || savedInfo.site_url
                    ? `Configured · ${savedInfo.shop_url || savedInfo.site_url}`
                    : 'Credentials saved'}
              </p>
            ) : (
              <p className="text-xs text-gray-400">Not configured — click to set up</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {savedInfo?.scheduled_sync && (
            <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">Hourly sync ON</span>
          )}
          {open ? <ChevronLeft className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {open && (
        <div className="border-t p-5 space-y-5">
          {/* Status banner — only show on explicit test result */}
          {testResult && (
            <div className={`rounded-lg p-3 flex items-start gap-2 ${testResult.ok ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
              {testResult.ok
                ? <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                : <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />}
              <p className={`text-xs ${testResult.ok ? 'text-emerald-800' : 'text-amber-800'}`}>
                {testResult.ok
                  ? <><strong>Connected!</strong> {testResult.msg}</>
                  : <><strong>Failed:</strong> {testResult.msg}</>}
              </p>
            </div>
          )}
          {savedInfo && !testResult && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <p className="text-xs text-emerald-800">
                <strong>Credentials saved.</strong> Token shown as ●●●● below. Click <strong>"Test &amp; Save"</strong> to verify, or scroll down and click <strong>"Pull from Shopify"</strong> to sync.
              </p>
            </div>
          )}

          {/* Credentials */}
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">Credentials</p>
            <div className="grid grid-cols-1 gap-3">
              {fields.map(f => (
                <div key={f.key}>
                  <label className="text-xs font-medium text-gray-600 mb-1 flex items-center justify-between">
                    <span>{f.label}</span>
                    {f.secret && form[f.key] && (
                      <span className={`text-[10px] font-mono ${
                        form[f.key].includes('*')     ? 'text-emerald-600'
                        : form[f.key].length < 20     ? 'text-red-500'
                        : form[f.key].length < 35     ? 'text-amber-500'
                        : 'text-emerald-600'
                      }`}>
                        {form[f.key].includes('*')
                          ? 'Token saved ✓ (shown as ●●●●)'
                          : `${form[f.key].length} chars ${form[f.key].length < 20 ? '⚠ too short' : form[f.key].length < 35 ? '⚠ check length' : '✓ looks good'}`
                        }
                      </span>
                    )}
                    {f.secret && savedInfo && !form[f.key] && (
                      <span className="text-[10px] text-amber-600">Saved — paste new token to update</span>
                    )}
                  </label>
                  <input type={f.secret ? 'password' : 'text'}
                    placeholder={f.secret && savedInfo && !form[f.key] ? '(token saved — paste new token to update)' : f.placeholder}
                    autoComplete={f.secret ? 'new-password' : 'off'}
                    value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              <button onClick={test} disabled={testing}
                className="flex items-center gap-2 border px-4 py-2 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50">
                <Plug className={`w-4 h-4 ${testing ? 'animate-pulse' : ''}`} />
                {testing ? 'Testing...' : 'Test & Save'}
              </button>
              <button onClick={async () => {
                if (!form.shop_url) { setTestResult({ ok: false, msg: 'Enter Shop URL first' }); return; }
                // Build payload: skip masked tokens (backend already has real token stored)
                const payload = { platform, shop_url: form.shop_url };
                fields.filter(f => f.secret).forEach(f => {
                  const val = form[f.key];
                  if (val && !val.includes('*') && val.length >= 6) {
                    payload[f.key] = val.replace(/[^\x20-\x7E]/g, '').trim();
                  }
                });
                const hasToken = fields.some(f => f.secret && payload[f.key]);
                const hasSavedToken = savedInfo && fields.some(f => f.secret && savedInfo[f.key]);
                if (!hasToken && !hasSavedToken) {
                  setTestResult({ ok: false, msg: 'Enter the Admin API Token first' }); return;
                }
                try {
                  await axios.post(`${API}/connectors/save`, hasToken ? payload : { platform, shop_url: form.shop_url });
                  setSavedInfo(p => ({ ...p, ...payload }));
                  setTestResult({ ok: true, msg: 'Credentials saved! Click "Test & Save" to verify or "Pull from Shopify" to sync.' });
                } catch (e) {
                  setTestResult({ ok: false, msg: e.response?.data?.detail || 'Save failed' });
                }
              }} className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-600">
                Save Credentials
              </button>
              {savedInfo && (
                <button onClick={async () => {
                  if (!window.confirm('Delete saved ' + label + ' credentials?')) return;
                  await axios.delete(`${API}/connectors/${platform}`);
                  setSavedInfo(null); setTestResult(null); setForm({});
                }} className="text-xs text-red-400 hover:text-red-600 border border-red-200 px-3 py-2 rounded-lg hover:bg-red-50">
                  Delete Saved
                </button>
              )}
            </div>
            {testResult && (
              <div className={`mt-2 text-xs rounded-lg px-3 py-2 flex items-center gap-2 ${testResult.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                {testResult.ok ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                {testResult.msg}
              </div>
            )}
          </div>

          {/* Manual sync */}
          {isConnected && (
            <div className="space-y-4">
              {/* ── ⇅ SYNC NOW (primary one-click) ── */}
              {platform === 'shopify' && (
                <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-blue-50 rounded-2xl border border-indigo-200 p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <p className="text-xs font-bold text-indigo-900 uppercase tracking-wider">One-Click Bi-Directional Sync</p>
                      <p className="text-[11px] text-indigo-700 mt-1">
                        Pulls new orders, customers &amp; product metadata. Compares stock levels &amp; surfaces
                        any differences for you to resolve.
                      </p>
                    </div>
                    {savedInfo?.last_synced && (
                      <span className="text-[10px] text-indigo-600 shrink-0 whitespace-nowrap">
                        Last: {new Date(savedInfo.last_synced).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <button onClick={async () => {
                      setSyncing(p => ({ ...p, sync_now: true }));
                      setSyncRes({});   // clear old results
                      try {
                        const r = await axios.post(`${API}/connectors/shopify/sync-now`, form);
                        setSyncRes({ sync_now: r.data });
                      } catch (e) {
                        setSyncRes({ sync_now: { error: e.response?.data?.detail || 'Sync failed' } });
                      }
                      setSyncing(p => ({ ...p, sync_now: false }));
                    }}
                    disabled={syncing.sync_now}
                    data-testid="sync-now-btn"
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-xl text-sm font-bold hover:from-indigo-700 hover:to-purple-700 disabled:opacity-60 shadow-md transition-all">
                    <RefreshCw className={`w-4 h-4 ${syncing.sync_now ? 'animate-spin' : ''}`} />
                    {syncing.sync_now ? 'Syncing with Shopify...' : 'Sync Now'}
                  </button>

                  {/* Result summary */}
                  {syncRes.sync_now && !syncRes.sync_now.error && (
                    <div className="mt-3 space-y-2">
                      <div className="bg-white rounded-xl border border-indigo-100 p-3">
                        <p className="text-xs font-semibold text-indigo-900">{syncRes.sync_now.message}</p>
                        <div className="grid grid-cols-4 gap-2 mt-2">
                          {[
                            { label: 'New orders',    v: syncRes.sync_now.new_orders,     color: 'blue'    },
                            { label: 'New products',  v: syncRes.sync_now.new_products,   color: 'emerald' },
                            { label: 'New customers', v: syncRes.sync_now.new_customers,  color: 'purple'  },
                            { label: 'Stock diffs',   v: syncRes.sync_now.conflicts?.length || 0, color: (syncRes.sync_now.conflicts?.length ? 'amber' : 'gray') },
                          ].map(s => (
                            <div key={s.label} className={`rounded-lg p-2 text-center bg-${s.color}-50`}>
                              <p className={`text-xl font-bold text-${s.color}-700`}>{s.v}</p>
                              <p className={`text-[9px] text-${s.color}-600 uppercase tracking-wider mt-0.5`}>{s.label}</p>
                            </div>
                          ))}
                        </div>

                        {/* ── Per-item breakdown ── */}
                        <div className="space-y-2 mt-3">
                          <SyncResultDetails
                            label="New orders"
                            items={syncRes.sync_now.new_orders_list}
                            color="blue"
                            columns={[
                              { key: 'order_id',    label: 'Order ID' },
                              { key: 'customer',    label: 'Customer' },
                              { key: 'items_count', label: 'Items' },
                              { key: 'total',       label: 'Total',  render: r => `$${(r.total || 0).toLocaleString()}` },
                            ]}
                          />
                          <SyncResultDetails
                            label="New products"
                            items={syncRes.sync_now.new_products_list}
                            color="emerald"
                            columns={[
                              { key: 'name', label: 'Product' },
                              { key: 'sku',  label: 'SKU' },
                            ]}
                          />
                          <SyncResultDetails
                            label="New customers"
                            items={syncRes.sync_now.new_customers_list}
                            color="purple"
                            columns={[
                              { key: 'name',  label: 'Name' },
                              { key: 'email', label: 'Email' },
                            ]}
                          />
                        </div>
                      </div>

                      {/* Inline conflict resolver when diffs exist */}
                      {syncRes.sync_now.conflicts?.length > 0 && (
                        <InventoryDiffTable
                          data={{
                            items:       syncRes.sync_now.conflicts,
                            in_sync:     syncRes.sync_now.in_sync,
                            out_of_sync: syncRes.sync_now.conflicts.length,
                            total:       syncRes.sync_now.total_linked,
                          }}
                          form={form}
                          onApplied={(r) => setSyncRes(p => ({ ...p, sync_now: null, apply_sync: r }))}
                        />
                      )}
                    </div>
                  )}
                  {syncRes.sync_now?.error && (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
                      <strong>Sync failed:</strong> {syncRes.sync_now.error}
                    </div>
                  )}
                  {syncRes.apply_sync && !syncRes.apply_sync?.error && (
                    <p className="mt-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                      {syncRes.apply_sync.message}
                    </p>
                  )}
                </div>
              )}

              {/* ── WooCommerce fallback (no sync-now yet) ── */}
              {platform !== 'shopify' && (
                <div className="bg-blue-50 rounded-xl border border-blue-100 p-4">
                  <p className="text-xs font-bold text-blue-800 uppercase tracking-wider mb-3">Pull Data from {label}</p>
                  <button onClick={() => { ['products', 'customers', 'orders'].forEach(e => sync(e)); }}
                    disabled={Object.values(syncing).some(v => v === true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 shadow-sm"
                    data-testid="pull-all-btn">
                    <Play className={`w-3.5 h-3.5 ${Object.values(syncing).some(v => v === true) ? 'animate-pulse' : ''}`} />
                    {Object.values(syncing).some(v => v === true) ? 'Syncing...' : 'Products + Orders + Customers'}
                  </button>
                  <div className="mt-2 space-y-1">
                    {Object.entries(syncRes).map(([entity, r]) => r && !r.error && (
                      <span key={entity} className="text-[10px] text-emerald-600 mr-2">Synced {r.imported || 0} {entity}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Advanced (collapsed) ── */}
              {platform === 'shopify' && (
                <AdvancedSyncDrawer
                  form={form}
                  syncing={syncing}
                  setSyncing={setSyncing}
                  syncRes={syncRes}
                  setSyncRes={setSyncRes}
                  API={API}
                  sync={sync}
                />
              )}

              {/* ── Sync History ── */}
              {platform === 'shopify' && (
                <SyncHistoryPanel platform={platform} API={API} />
              )}
            </div>
          )}

          {/* Scheduled sync */}
          {isConnected && (
            <div className="bg-purple-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-purple-800 flex items-center gap-1.5">
                    <RefreshCw className="w-3.5 h-3.5" />Scheduled Auto-Sync
                  </p>
                  <p className="text-xs text-purple-600 mt-0.5">
                    Automatically pull new data every hour via Celery Beat
                    {schedule.enabled && (
                      <span className="ml-1 font-semibold">— next run at {nextRunTime()}</span>
                    )}
                  </p>
                </div>
                <button onClick={() => { setSchedule(p => ({ ...p, enabled: !p.enabled })); setScheduleMsg(''); }}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all shadow-sm ${schedule.enabled ? 'bg-purple-600 text-white' : 'bg-white text-purple-600 border border-purple-300 hover:bg-purple-100'}`}>
                  {schedule.enabled ? '● ON' : '○ OFF'}
                </button>
              </div>

              {schedule.enabled && (
                <div>
                  <p className="text-xs text-purple-700 font-medium mb-1.5">Sync which data every hour?</p>
                  <div className="flex flex-wrap gap-2">
                    {['orders', 'products', 'customers'].map(e => (
                      <button key={e} onClick={() => toggleEntity(e)}
                        className={`text-xs px-3 py-1 rounded-full border transition-all ${schedule.entities.includes(e) ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-purple-600 border-purple-300 hover:bg-purple-50'}`}>
                        {schedule.entities.includes(e) ? '✓ ' : ''}{e}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 flex-wrap">
                <button onClick={saveSchedule} disabled={scheduleSaving}
                  className="flex items-center gap-1.5 bg-purple-600 text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-purple-700 disabled:bg-gray-400">
                  {scheduleSaving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                  {scheduleSaving ? 'Saving...' : 'Save Schedule'}
                </button>
                <button onClick={triggerNow} disabled={runningNow}
                  className="flex items-center gap-1.5 border border-purple-400 text-purple-700 bg-white px-4 py-2 rounded-lg text-xs font-medium hover:bg-purple-50 disabled:opacity-50">
                  <Play className={`w-3.5 h-3.5 ${runningNow ? 'animate-pulse' : ''}`} />
                  {runningNow ? 'Running...' : 'Trigger Now'}
                </button>
              </div>

              {scheduleMsg && (
                <div className={`text-xs rounded-lg px-3 py-2 flex items-start gap-2 ${scheduleMsg.startsWith('✓') ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                  {scheduleMsg.startsWith('✓') ? <CheckCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                  <span>{scheduleMsg}</span>
                </div>
              )}

              <p className="text-[10px] text-purple-400 pt-1 border-t border-purple-200">
                Celery Beat runs at :30 every hour · "Trigger Now" runs immediately · Only new records imported (no duplicates)
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

import ShopeeTab from './ShopeeTab';

export default function ImportTab({ products = [], tz = 'Asia/Singapore' }) {
  const [view, setView]     = useState(() => localStorage.getItem('wms_import_view') || 'choose');
  const [openConnector, setOpenConnector] = useState(() => localStorage.getItem('wms_import_connector') || '');

  // CSV wizard state
  const [entity, setEntity]   = useState('');
  const [step, setStep]       = useState(1);        // 1=upload 2=map 3=preview 4=result
  const [csvData, setCsvData] = useState({ headers: [], rows: [] });
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [result, setResult]   = useState(null);
  const fileRef = useRef();

  const entityConf = ENTITIES[entity] || {};

  // Download template
  const downloadTemplate = async (ent) => {
    window.open(`${API}/import/template/${ent}`, '_blank');
  };

  // Handle CSV upload
  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(e.target.result);
      setCsvData(parsed);
      // Auto-map: find CSV headers that match Agora field keys or labels
      const autoMap = {};
      for (const f of (entityConf.fields || [])) {
        const match = parsed.headers.find(h =>
          h.toLowerCase().replace(/[\s_-]/g, '') === f.key.replace(/_/g, '') ||
          h.toLowerCase() === f.label.toLowerCase().replace(' *', '').toLowerCase()
        );
        if (match) autoMap[f.key] = match;
      }
      setMapping(autoMap);
      setStep(2);
    };
    reader.readAsText(file);
  };

  // Apply mapping to row
  const mapRow = (rawRow) => {
    const out = {};
    for (const [field, col] of Object.entries(mapping)) {
      if (col && rawRow[col] !== undefined) out[field] = rawRow[col];
    }
    return out;
  };

  const doImport = async () => {
    setImporting(true);
    const rows = csvData.rows.map(mapRow);
    try {
      const r = await axios.post(`${API}/import/${entity}`, { rows });
      setResult(r.data);
      setStep(4);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || 'Import failed' });
      setStep(4);
    }
    setImporting(false);
  };

  const setViewAndCache = (v) => { setView(v); localStorage.setItem('wms_import_view', v); };

  const reset = () => {
    setStep(1); setCsvData({ headers: [], rows: [] }); setMapping({}); setResult(null); setEntity('');
    setViewAndCache('choose');
    localStorage.removeItem('wms_import_connector');
  };

  const missingRequired = (entityConf.fields || [])
    .filter(f => f.required && !mapping[f.key]);

  const CONNECTORS = [
    {
      platform: 'shopify', label: 'Shopify', logo: '🛍️',
      fields: [
        { key: 'shop_url',     label: 'Shop URL',        placeholder: 'yourstore.myshopify.com  (NOT your email — your Shopify store handle)' },
        { key: 'access_token', label: 'Admin API Token', placeholder: 'shpat_xxxxxxxxxxxx', secret: true },
      ]
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold">Import & Connect</h2>
        <p className="text-sm text-gray-500 mt-1">Migrate data from your existing systems or connect live integrations.</p>
      </div>

      {/* ── CHOOSE ── */}
      {view === 'choose' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <button onClick={() => setViewAndCache('csv')}
            className="bg-white rounded-xl border shadow-sm p-6 text-left hover:border-blue-400 hover:shadow-md transition-all group">
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-100">
              <Upload className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-bold text-gray-900 mb-1">CSV / Excel Import</h3>
            <p className="text-sm text-gray-500">Universal — works with any system that can export CSV. Import products, customers, suppliers, orders, and purchase history.</p>
            <div className="flex flex-wrap gap-1 mt-3">
              {Object.values(ENTITIES).map(e => (
                <span key={e.label} className={`text-[10px] px-2 py-0.5 rounded-full bg-${e.color}-100 text-${e.color}-700`}>{e.label}</span>
              ))}
            </div>
          </button>

          <button onClick={() => setViewAndCache('api')}
            className="bg-white rounded-xl border shadow-sm p-6 text-left hover:border-purple-400 hover:shadow-md transition-all group">
            <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-100">
              <Plug className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-bold text-gray-900 mb-1">API Connectors</h3>
            <p className="text-sm text-gray-500">Connect live to popular platforms. Products, customers and orders sync automatically from your existing store.</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {[['Shopify', '🛍️'], ['Shopee', '🧡'], ['WooCommerce', '🛒']].map(([n, e]) => (
                <span key={n} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{e} {n}</span>
              ))}
              <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">+ more coming</span>
            </div>
          </button>

          <button onClick={() => setViewAndCache('shopee')}
            className="bg-white rounded-xl border shadow-sm p-6 text-left hover:border-orange-400 hover:shadow-md transition-all group">
            <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-orange-100">
              <span className="text-2xl">🛒</span>
            </div>
            <h3 className="font-bold text-gray-900 mb-1">Shopee Marketplace</h3>
            <p className="text-sm text-gray-500">Sync orders, products and inventory with Shopee. Includes a simulator for testing without real API credentials.</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {['Orders','Products','Webhooks','Simulator'].map(t => (
                <span key={t} className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">{t}</span>
              ))}
            </div>
          </button>
        </div>
      )}

      {/* ── CSV IMPORT ── */}
      {view === 'csv' && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          {/* Progress bar */}
          <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={reset} className="text-gray-400 hover:text-gray-600"><ChevronLeft className="w-4 h-4" /></button>
              <div className="flex items-center gap-2">
                {['Choose type', 'Upload CSV', 'Map fields', 'Preview & Import', 'Done'].map((s, i) => (
                  <React.Fragment key={s}>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${step === i + 0 || (i === 0 && !entity) ? 'bg-blue-500 text-white' : step > i ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>{s}</span>
                    {i < 4 && <ChevronRight className="w-3 h-3 text-gray-300" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Step 0: Choose entity */}
            {!entity && (
              <div>
                <p className="text-sm text-gray-600 mb-4 font-medium">What data are you importing?</p>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                  {Object.entries(ENTITIES).map(([key, conf]) => (
                    <button key={key} onClick={() => { setEntity(key); setStep(1); }}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border hover:border-${conf.color}-400 hover:bg-${conf.color}-50 transition-all`}>
                      <conf.icon className={`w-8 h-8 text-${conf.color}-500`} />
                      <span className="text-sm font-medium">{conf.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 1: Upload */}
            {entity && step === 1 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <entityConf.icon className={`w-6 h-6 text-${entityConf.color}-500`} />
                  <h3 className="font-semibold">Import {entityConf.label}</h3>
                  <button onClick={() => downloadTemplate(entity)} className="flex items-center gap-1 text-xs text-blue-600 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-50 ml-auto">
                    <Download className="w-3 h-3" />Download Template
                  </button>
                </div>

                <div className="border-2 border-dashed border-gray-200 rounded-xl p-10 text-center hover:border-blue-300 transition-colors cursor-pointer"
                  onClick={() => fileRef.current?.click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}>
                  <Upload className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium text-gray-600">Drop your CSV file here</p>
                  <p className="text-sm text-gray-400 mt-1">or click to browse · CSV, TSV, or semicolon-separated</p>
                  <input ref={fileRef} type="file" accept=".csv,.tsv,.txt" className="hidden"
                    onChange={e => handleFile(e.target.files[0])} />
                </div>

                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-blue-800 mb-2">Required columns for {entityConf.label}:</p>
                  <div className="flex flex-wrap gap-1">
                    {(entityConf.fields || []).map(f => (
                      <span key={f.key} className={`text-[10px] px-2 py-0.5 rounded-full ${f.required ? 'bg-blue-200 text-blue-800 font-semibold' : 'bg-blue-100 text-blue-600'}`}>
                        {f.label.replace(' *', '')}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Map fields */}
            {entity && step === 2 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Map Your Columns → Agora Fields</h3>
                  <span className="text-xs text-gray-400">{csvData.rows.length} rows detected · {csvData.headers.length} columns</span>
                </div>
                <div className="space-y-2">
                  {(entityConf.fields || []).map(f => (
                    <div key={f.key} className="flex items-center gap-3">
                      <div className="w-44 shrink-0">
                        <span className={`text-sm ${f.required ? 'font-semibold' : 'text-gray-600'}`}>{f.label}</span>
                      </div>
                      <select value={mapping[f.key] || ''} onChange={e => setMapping(p => ({ ...p, [f.key]: e.target.value }))}
                        className={`flex-1 border rounded-lg px-3 py-2 text-sm ${f.required && !mapping[f.key] ? 'border-amber-300 bg-amber-50' : ''}`}>
                        <option value="">— Not mapped —</option>
                        {csvData.headers.map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                      {mapping[f.key] && <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />}
                      {f.required && !mapping[f.key] && <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />}
                    </div>
                  ))}
                </div>
                {missingRequired.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                    Required fields not mapped: {missingRequired.map(f => f.label).join(', ')}
                  </div>
                )}
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setStep(1)} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Back</button>
                  <button onClick={() => setStep(3)} disabled={missingRequired.length > 0}
                    className="px-6 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 font-medium">
                    Preview →
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Preview + Import */}
            {entity && step === 3 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Preview (first 5 rows)</h3>
                  <span className="text-xs text-gray-400">{csvData.rows.length} rows will be imported</span>
                </div>
                <div className="overflow-x-auto border rounded-xl">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>{(entityConf.fields || []).filter(f => mapping[f.key]).map(f => (
                        <th key={f.key} className="px-3 py-2 text-left text-gray-500 font-medium uppercase">{f.label.replace(' *', '')}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y">
                      {csvData.rows.slice(0, 5).map((row, i) => {
                        const mapped = mapRow(row);
                        return (
                          <tr key={i} className="hover:bg-gray-50">
                            {(entityConf.fields || []).filter(f => mapping[f.key]).map(f => (
                              <td key={f.key} className="px-3 py-2 text-gray-700">{mapped[f.key] || <span className="text-gray-300">—</span>}</td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setStep(2)} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Back</button>
                  <button onClick={doImport} disabled={importing}
                    className="flex items-center gap-2 px-6 py-2 text-sm bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:bg-gray-300 font-medium">
                    <Play className={`w-4 h-4 ${importing ? 'animate-pulse' : ''}`} />
                    {importing ? 'Importing...' : `Import ${csvData.rows.length} rows`}
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Results */}
            {step === 4 && result && (
              <div className="space-y-4">
                {result.error ? (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                    <p className="font-semibold text-red-800 flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Import Failed</p>
                    <p className="text-sm text-red-700 mt-1">{result.error}</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-emerald-50 rounded-xl p-4 text-center">
                        <p className="text-3xl font-bold text-emerald-700">{result.imported}</p>
                        <p className="text-xs text-emerald-600 mt-1">Imported</p>
                      </div>
                      <div className="bg-amber-50 rounded-xl p-4 text-center">
                        <p className="text-3xl font-bold text-amber-700">{result.skipped}</p>
                        <p className="text-xs text-amber-600 mt-1">Skipped (already exist)</p>
                      </div>
                      <div className={`${result.errors > 0 ? 'bg-red-50' : 'bg-gray-50'} rounded-xl p-4 text-center`}>
                        <p className={`text-3xl font-bold ${result.errors > 0 ? 'text-red-700' : 'text-gray-400'}`}>{result.errors}</p>
                        <p className="text-xs text-gray-500 mt-1">Errors</p>
                      </div>
                    </div>
                    {result.error_list?.length > 0 && (
                      <div className="bg-red-50 rounded-xl p-4 space-y-1">
                        <p className="text-xs font-semibold text-red-700 mb-2">Row errors:</p>
                        {result.error_list.map((e, i) => (
                          <p key={i} className="text-xs text-red-600">Row {e.row}: {e.error}</p>
                        ))}
                      </div>
                    )}
                    <div className="bg-blue-50 rounded-xl p-4">
                      <p className="text-sm text-blue-700">
                        <strong>{result.imported} {result.entity}</strong> successfully imported to Agora WMS.
                        {result.skipped > 0 ? ` ${result.skipped} were skipped because they already exist (matched by key field).` : ''}
                      </p>
                    </div>
                  </>
                )}
                <div className="flex gap-3">
                  <button onClick={() => { setStep(1); setResult(null); }} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Import More</button>
                  <button onClick={reset} className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600">Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── API CONNECTORS ── */}
      {view === 'api' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <button onClick={reset} className="text-gray-400 hover:text-gray-600"><ChevronLeft className="w-4 h-4" /></button>
            <h3 className="font-semibold">API Connectors</h3>
            <span className="text-xs text-gray-400">Enter credentials → Test → Sync data</span>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
            <strong>How it works:</strong> Enter your store credentials → click "Test &amp; Save Connection" → if successful, sync products, customers, and orders directly into Agora WMS. Existing records are never duplicated.
          </div>

          {CONNECTORS.map(c => (
            <ConnectorCard key={c.platform} platform={c.platform} label={c.label} logo={c.logo} fields={c.fields}
              defaultOpen={openConnector === c.platform}
              onToggleOpen={(isOpen) => {
                const val = isOpen ? c.platform : '';
                setOpenConnector(val);
                localStorage.setItem('wms_import_connector', val);
              }}
            />
          ))}

          {/* Shopee — Coming Soon */}
          <div className="bg-white rounded-xl border border-orange-200 px-5 py-4 flex items-center justify-between" data-testid="shopee-coming-soon">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🧡</span>
              <div>
                <p className="font-medium text-gray-800">Shopee Open Platform</p>
                <p className="text-xs text-gray-500">Sync products, orders, and fulfillment from Shopee Seller Centre</p>
              </div>
            </div>
            <span className="text-xs font-bold uppercase tracking-wider text-orange-500 bg-orange-50 px-3 py-1.5 rounded-full border border-orange-200">Coming Soon</span>
          </div>

          {/* Coming soon placeholders */}
          {[
            { label: 'WooCommerce',  logo: '🛒', note: 'Sync products, customers and orders from WordPress/WooCommerce' },
            { label: 'QuickBooks', logo: '📊', note: 'Sync customers, items, invoices' },
            { label: 'Odoo ERP',   logo: '⚙️', note: 'Sync products, customers, purchase orders' },
            { label: 'SAP / Custom REST API', logo: '🔗', note: 'Generic REST connector — configure endpoint + auth headers' },
          ].map(p => (
            <div key={p.label} className="bg-white rounded-xl border border-dashed border-gray-200 px-5 py-4 flex items-center justify-between opacity-60">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{p.logo}</span>
                <div>
                  <p className="font-medium text-gray-700">{p.label}</p>
                  <p className="text-xs text-gray-400">{p.note}</p>
                </div>
              </div>
              <span className="text-xs bg-gray-100 text-gray-500 px-3 py-1 rounded-full font-medium">Coming soon</span>
            </div>
          ))}
        </div>
      )}

      {/* ── SHOPEE MARKETPLACE ── */}
      {view === 'shopee' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <button onClick={reset} className="text-gray-400 hover:text-gray-600"><ChevronLeft className="w-4 h-4" /></button>
            <h3 className="font-semibold flex items-center gap-2"><span className="text-xl">🛒</span>Shopee Marketplace</h3>
          </div>
          <ShopeeTab products={products} tz={tz} />
        </div>
      )}
    </div>
  );
}


function Sparkline({ label, values, last, unit = '', color = '#6366f1', anomaly = false, anomalyTitle = '' }) {
  // Pure SVG sparkline — no chart library dependency
  if (!values || values.length === 0) return null;
  const W = 160, H = 36, PAD = 2;
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? (W - PAD * 2) / (values.length - 1) : 0;
  const pts = values.map((v, i) => {
    const x = PAD + i * step;
    const y = H - PAD - (v / max) * (H - PAD * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const areaPts = `${PAD},${H - PAD} ${pts} ${(PAD + (values.length - 1) * step).toFixed(1)},${H - PAD}`;
  const lastVal = values[values.length - 1];
  const lastX = PAD + (values.length - 1) * step;
  const lastY = H - PAD - (lastVal / max) * (H - PAD * 2);
  const accent = anomaly ? '#dc2626' : color;   // red-600 when anomaly

  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
        <span className={`text-sm font-bold ${anomaly ? 'animate-pulse' : ''}`} style={{ color: accent }}
          title={anomalyTitle || undefined}>
          {last}{unit}
        </span>
      </div>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block">
        <polygon points={areaPts} fill={accent} fillOpacity="0.12" />
        <polyline points={pts} fill="none" stroke={accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={lastX} cy={lastY} r={anomaly ? 3 : 2} fill={accent} />
      </svg>
    </div>
  );
}


function SyncHistoryPanel({ platform, API }) {
  const [open, setOpen]     = React.useState(false);
  const [history, setHistory] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [expanded, setExpanded] = React.useState(null);  // sync_id of expanded row
  const [detail, setDetail]     = React.useState(null);
  const [stats, setStats]       = React.useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [h, s] = await Promise.all([
        axios.get(`${API}/connectors/sync-history?platform=${platform}&limit=50`),
        axios.get(`${API}/connectors/sync-history/stats?platform=${platform}&days=30`),
      ]);
      setHistory(h.data || []);
      setStats(s.data || null);
    } catch { setHistory([]); setStats(null); }
    setLoading(false);
  };

  React.useEffect(() => { if (open) load(); }, [open, platform]);   // eslint-disable-line

  // Eagerly fetch stats-only (lightweight) so the anomaly bell shows even before user opens the panel
  React.useEffect(() => {
    axios.get(`${API}/connectors/sync-history/stats?platform=${platform}&days=30`)
      .then(r => setStats(s => s || r.data))
      .catch(() => {});
  }, [platform]);   // eslint-disable-line

  const openDetail = async (sync_id) => {
    if (expanded === sync_id) { setExpanded(null); setDetail(null); return; }
    setExpanded(sync_id); setDetail(null);
    try {
      const r = await axios.get(`${API}/connectors/sync-history/${sync_id}`);
      setDetail(r.data);
    } catch { setDetail({ error: 'Failed to load' }); }
  };

  const deleteEntry = async (sync_id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this sync history entry?')) return;
    await axios.delete(`${API}/connectors/sync-history/${sync_id}`);
    setHistory(h => h.filter(x => x.sync_id !== sync_id));
    if (expanded === sync_id) { setExpanded(null); setDetail(null); }
  };

  const downloadJSON = async (sync_id, e) => {
    e.stopPropagation();
    const r = await axios.get(`${API}/connectors/sync-history/${sync_id}`);
    const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `agora-${sync_id}.json`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const ACTION_META = {
    sync_now:       { label: 'Sync Now',        color: 'indigo'  },
    push_inventory: { label: 'Push Stock',      color: 'emerald' },
    push_skus:      { label: 'Push SKUs',       color: 'emerald' },
    pull_inventory: { label: 'Pull Stock',      color: 'blue'    },
    pull_full:      { label: 'Pull Full',       color: 'blue'    },
    inventory_diff: { label: 'Compare Stock',   color: 'amber'   },
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid="sync-history-panel">
      <button onClick={() => setOpen(v => !v)}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 text-left">
        <span className="text-xs font-semibold text-gray-600 flex items-center gap-2">
          <History className="w-3.5 h-3.5" />Sync History
          {history.length > 0 && open && <span className="text-[10px] text-gray-400">({history.length} runs)</span>}
          {stats?.anomaly?.active && (
            <span
              title={`Anomaly detected — today: ${stats.anomaly.today_conflicts} conflicts · 7-day avg: ${stats.anomaly.avg_7day}`}
              className="flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full animate-pulse"
              data-testid="sync-anomaly-badge">
              <Bell className="w-3 h-3" />Anomaly
            </span>
          )}
        </span>
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>

      {open && (
        <div className="p-3 bg-white border-t">
          {/* Sparkline trend — last 30 days */}
          {stats && stats.series && history.length > 0 && (
            <div className="mb-3 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-200 p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">30-Day Trend</p>
                <span className="text-[10px] text-gray-400">{stats.days} days · hover for daily values</span>
              </div>
              <div className="flex items-end gap-4">
                <Sparkline label="Syncs / day"  color="#6366f1"
                  values={stats.series.map(s => s.runs)}
                  last={stats.series.reduce((a, s) => a + s.runs, 0)} unit=" total" />
                <Sparkline label="Avg duration" color="#10b981"
                  values={stats.series.map(s => s.avg_ms)}
                  last={(() => {
                    const withRuns = stats.series.filter(s => s.runs > 0);
                    if (!withRuns.length) return '0';
                    return (withRuns.reduce((a, s) => a + s.avg_ms, 0) / withRuns.length / 1000).toFixed(1);
                  })()} unit="s" />
                <Sparkline label="Stock conflicts" color="#f59e0b"
                  values={stats.series.map(s => s.conflicts)}
                  last={stats.series.reduce((a, s) => a + s.conflicts, 0)} unit=" total"
                  anomaly={stats.anomaly?.active}
                  anomalyTitle={stats.anomaly?.active
                    ? `Anomaly: today has ${stats.anomaly.today_conflicts} conflicts vs 7-day avg of ${stats.anomaly.avg_7day}`
                    : ''} />
              </div>
            </div>
          )}

          {loading && <p className="text-xs text-gray-400 py-4 text-center">Loading...</p>}
          {!loading && history.length === 0 && (
            <p className="text-xs text-gray-400 py-4 text-center">No sync runs recorded yet. Click <strong>Sync Now</strong> to create your first entry.</p>
          )}
          {!loading && history.length > 0 && (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="w-full text-[11px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">When</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Action</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Summary</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">By</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-500 uppercase tracking-wide">Duration</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {history.map(h => {
                    const meta = ACTION_META[h.action] || { label: h.action, color: 'gray' };
                    const isExp = expanded === h.sync_id;
                    return (
                      <React.Fragment key={h.sync_id}>
                        <tr onClick={() => openDetail(h.sync_id)}
                          className={`cursor-pointer hover:bg-gray-50 ${isExp ? 'bg-gray-50' : ''}`}>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                            {new Date(h.started_at).toLocaleString([], { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold bg-${meta.color}-100 text-${meta.color}-700 uppercase tracking-wide`}>{meta.label}</span>
                            {h.status !== 'ok' && <span className="text-[10px] ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 uppercase">{h.status}</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-600 max-w-xs truncate">{h.summary}</td>
                          <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{h.triggered_by}</td>
                          <td className="px-3 py-2 text-right text-gray-400 whitespace-nowrap">{(h.duration_ms / 1000).toFixed(1)}s</td>
                          <td className="px-3 py-2 text-center whitespace-nowrap">
                            <button onClick={(e) => downloadJSON(h.sync_id, e)}
                              title="Download full JSON" className="text-gray-400 hover:text-blue-600 p-1"
                              data-testid={`history-dl-${h.sync_id}`}>
                              <Download className="w-3 h-3" />
                            </button>
                            <button onClick={(e) => deleteEntry(h.sync_id, e)}
                              title="Delete entry" className="text-gray-400 hover:text-red-600 p-1 ml-1"
                              data-testid={`history-del-${h.sync_id}`}>
                              <X className="w-3 h-3" />
                            </button>
                          </td>
                        </tr>
                        {isExp && detail && !detail.error && (
                          <tr className="bg-gray-50">
                            <td colSpan={6} className="px-4 py-3">
                              <SyncHistoryDetail detail={detail} />
                            </td>
                          </tr>
                        )}
                        {isExp && !detail && (
                          <tr className="bg-gray-50"><td colSpan={6} className="px-4 py-3 text-xs text-gray-400">Loading...</td></tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function SyncHistoryDetail({ detail }) {
  const r = detail.result || {};
  // Reuse SyncResultDetails — decide which tables to show based on action
  return (
    <div className="space-y-2">
      {detail.action === 'sync_now' && (
        <>
          <SyncResultDetails label="New orders" items={r.new_orders_list} color="blue"
            columns={[
              { key: 'order_id', label: 'Order ID' },
              { key: 'customer', label: 'Customer' },
              { key: 'items_count', label: 'Items' },
              { key: 'total', label: 'Total', render: x => `$${(x.total || 0).toLocaleString()}` },
            ]} />
          <SyncResultDetails label="New products" items={r.new_products_list} color="emerald"
            columns={[{ key: 'name', label: 'Product' }, { key: 'sku', label: 'SKU' }]} />
          <SyncResultDetails label="New customers" items={r.new_customers_list} color="purple"
            columns={[{ key: 'name', label: 'Name' }, { key: 'email', label: 'Email' }]} />
          <SyncResultDetails label="Stock conflicts" items={r.conflicts} color="amber"
            columns={[
              { key: 'name', label: 'Product' },
              { key: 'sku',  label: 'SKU' },
              { key: 'wms_stock', label: 'WMS' },
              { key: 'shopify_stock', label: 'Shopify' },
              { key: 'diff', label: 'Δ', render: x => (x.diff > 0 ? `+${x.diff}` : x.diff) },
            ]} />
        </>
      )}
      {(detail.action === 'push_inventory' || detail.action === 'push_skus') && (
        <>
          <SyncResultDetails label={detail.action === 'push_skus' ? 'SKUs pushed' : 'Stock levels pushed'}
            items={r.items_updated} color="emerald"
            columns={detail.action === 'push_skus'
              ? [{ key: 'name', label: 'Product' }, { key: 'sku', label: 'SKU' }, { key: 'variant_id', label: 'Variant ID' }]
              : [{ key: 'name', label: 'Product' }, { key: 'sku', label: 'SKU' }, { key: 'stock', label: 'Stock' }]} />
          <SyncResultDetails label="Failures" items={r.items_failed} color="red"
            columns={[{ key: 'name', label: 'Product' }, { key: 'sku', label: 'SKU' }, { key: 'error', label: 'Reason' }]} />
        </>
      )}
      {detail.action === 'pull_inventory' && (
        <SyncResultDetails label="Stock updated from Shopify" items={r.details} color="blue"
          columns={[
            { key: 'name', label: 'Product' },
            { key: 'sku', label: 'SKU' },
            { key: 'wms_before', label: 'WMS before' },
            { key: 'shopify', label: 'Shopify now' },
            { key: 'delta', label: 'Δ', render: x => (x.delta > 0 ? `+${x.delta}` : x.delta) },
          ]} />
      )}
      {detail.error && (
        <p className="text-[11px] text-red-700 bg-red-50 rounded px-2 py-1.5">Error: {detail.error}</p>
      )}
    </div>
  );
}


function SyncResultDetails({ label, items, columns, color = 'gray', emptyText }) {
  const [open, setOpen] = React.useState(false);
  if (!items || items.length === 0) {
    return emptyText ? <p className={`text-[11px] text-${color}-600 italic`}>{emptyText}</p> : null;
  }

  const downloadCSV = (e) => {
    e.stopPropagation();
    const esc = (v) => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = columns.map(c => esc(c.label)).join(',');
    const rows = items.map(row =>
      columns.map(c => esc(c.render ? c.render(row) : (row[c.key] ?? ''))).join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    const ts   = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    a.href = url;
    a.download = `agora-${label.toLowerCase().replace(/\s+/g, '-')}-${ts}.csv`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  return (
    <div className={`border border-${color}-200 rounded-lg overflow-hidden text-[11px]`} data-testid={`sync-details-${label.replace(/\s/g, '-')}`}>
      <div className={`w-full flex items-center justify-between bg-${color}-50 text-${color}-800`}>
        <button onClick={() => setOpen(v => !v)}
          className={`flex-1 flex items-center justify-between px-3 py-1.5 hover:bg-${color}-100 font-medium text-left`}>
          <span>{label} ({items.length})</span>
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </button>
        <button onClick={downloadCSV} title="Download as CSV"
          data-testid={`csv-${label.replace(/\s/g, '-')}`}
          className={`flex items-center gap-1 px-2.5 py-1.5 hover:bg-${color}-100 border-l border-${color}-200 text-[10px] font-medium`}>
          <Download className="w-3 h-3" />CSV
        </button>
      </div>
      {open && (
        <div className="max-h-44 overflow-y-auto bg-white">
          <table className="w-full">
            <thead className={`bg-${color}-50/50 sticky top-0`}>
              <tr>{columns.map(c => (
                <th key={c.key} className={`px-2 py-1 text-left font-semibold text-${color}-700 text-[10px] uppercase tracking-wide`}>{c.label}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y">
              {items.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  {columns.map(c => (
                    <td key={c.key} className="px-2 py-1 text-gray-700">
                      {c.render ? c.render(row) : (row[c.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function AdvancedSyncDrawer({ form, syncing, setSyncing, syncRes, setSyncRes, API, sync }) {
  const [open, setOpen] = React.useState(false);

  const runAction = async (key, url) => {
    setSyncing(p => ({ ...p, [key]: true }));
    try {
      const r = await axios.post(`${API}${url}`, form);
      setSyncRes(p => ({ ...p, [key]: r.data }));
    } catch (e) {
      setSyncRes(p => ({ ...p, [key]: { error: e.response?.data?.detail || 'Failed' } }));
    }
    setSyncing(p => ({ ...p, [key]: false }));
  };

  const actions = [
    { key: 'pull_full',       label: 'Pull Products + Orders + Customers',  desc: 'Full metadata refresh (overrides WMS stock with Shopify stock)', onClick: () => { ['products', 'customers', 'orders'].forEach(e => sync(e)); } },
    { key: 'pull_inventory',  label: 'Pull Stock Levels Only',               desc: 'Overwrites WMS stock with live Shopify values (no conflict check)', onClick: () => runAction('pull_inventory', '/connectors/shopify/pull-inventory') },
    { key: 'push_inventory',  label: 'Push Stock Levels to Shopify',         desc: 'Overwrites Shopify stock with current WMS values',                  onClick: () => runAction('push_inventory', '/connectors/shopify/push-inventory') },
    { key: 'push_skus',       label: 'Push SKUs to Shopify Variants',        desc: 'One-off: backfill missing SKUs on Shopify variants',                onClick: () => runAction('push_skus', '/connectors/shopify/push-skus') },
    { key: 'inventory_diff',  label: 'Full Inventory Comparison',             desc: 'Side-by-side WMS vs Shopify stock table with per-row decisions',    onClick: () => runAction('inventory_diff', '/connectors/shopify/inventory-diff') },
  ];

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid="advanced-sync-drawer">
      <button onClick={() => setOpen(v => !v)}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 text-left">
        <span className="text-xs font-semibold text-gray-600 flex items-center gap-2">
          <Settings2 className="w-3.5 h-3.5" />Advanced sync options
        </span>
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      {open && (
        <div className="p-4 space-y-2 border-t bg-white">
          <p className="text-[11px] text-gray-500 mb-2">
            For power users. Most workflows are covered by <strong>Sync Now</strong>.
          </p>
          {actions.map(a => (
            <div key={a.key} className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-gray-50">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-800">{a.label}</p>
                <p className="text-[11px] text-gray-500 leading-tight">{a.desc}</p>
              </div>
              <button onClick={a.onClick}
                disabled={syncing[a.key] || (a.key === 'pull_full' && Object.values(syncing).some(v => v === true))}
                className="text-[11px] bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg font-medium text-gray-700 disabled:opacity-50 shrink-0"
                data-testid={`adv-${a.key}`}>
                {syncing[a.key] ? 'Running...' : 'Run'}
              </button>
            </div>
          ))}

          {/* Advanced results summary */}
          <div className="pt-2 mt-2 border-t space-y-2">
            {syncRes.push_inventory && !syncRes.push_inventory?.error && (
              <div className="space-y-1">
                <p className="text-[11px] text-emerald-700 bg-emerald-50 rounded px-2 py-1.5">{syncRes.push_inventory.message}</p>
                <SyncResultDetails
                  label="Stock levels pushed"
                  items={syncRes.push_inventory.items_updated}
                  color="emerald"
                  columns={[
                    { key: 'name',  label: 'Product' },
                    { key: 'sku',   label: 'SKU' },
                    { key: 'stock', label: 'New Shopify stock' },
                  ]}
                />
                <SyncResultDetails
                  label="Push failures"
                  items={syncRes.push_inventory.items_failed}
                  color="red"
                  columns={[
                    { key: 'name',  label: 'Product' },
                    { key: 'sku',   label: 'SKU' },
                    { key: 'error', label: 'Reason' },
                  ]}
                />
              </div>
            )}
            {syncRes.push_skus && !syncRes.push_skus?.error && (
              <div className="space-y-1">
                <p className="text-[11px] text-emerald-700 bg-emerald-50 rounded px-2 py-1.5">{syncRes.push_skus.message}</p>
                <SyncResultDetails
                  label="SKUs pushed"
                  items={syncRes.push_skus.items_updated}
                  color="emerald"
                  columns={[
                    { key: 'name',       label: 'Product' },
                    { key: 'sku',        label: 'SKU' },
                    { key: 'variant_id', label: 'Shopify variant' },
                  ]}
                />
                <SyncResultDetails
                  label="SKU push failures"
                  items={syncRes.push_skus.items_failed}
                  color="red"
                  columns={[
                    { key: 'name',  label: 'Product' },
                    { key: 'sku',   label: 'SKU' },
                    { key: 'error', label: 'Reason' },
                  ]}
                />
              </div>
            )}
            {syncRes.pull_inventory && !syncRes.pull_inventory?.error && (
              <div className="space-y-1">
                <p className="text-[11px] text-blue-700 bg-blue-50 rounded px-2 py-1.5">{syncRes.pull_inventory.message}</p>
                <SyncResultDetails
                  label="Stock updated from Shopify"
                  items={syncRes.pull_inventory.details}
                  color="blue"
                  columns={[
                    { key: 'name',       label: 'Product' },
                    { key: 'sku',        label: 'SKU' },
                    { key: 'wms_before', label: 'WMS (before)' },
                    { key: 'shopify',    label: 'Shopify (now)' },
                    { key: 'delta',      label: 'Δ', render: r => (r.delta > 0 ? `+${r.delta}` : r.delta) },
                  ]}
                />
              </div>
            )}
            {Object.entries(syncRes).filter(([, r]) => r?.error).map(([k, r]) => (
              <p key={k} className="text-[11px] text-red-600 bg-red-50 rounded px-2 py-1.5">{k}: {r.error}</p>
            ))}
            {syncRes.inventory_diff && !syncRes.inventory_diff?.error && syncRes.inventory_diff.items && (
              <InventoryDiffTable data={syncRes.inventory_diff} form={form}
                onApplied={(r) => setSyncRes(p => ({ ...p, inventory_diff: null, apply_sync: r }))} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function InventoryDiffTable({ data, form, onApplied }) {
  const [decisions, setDecisions] = React.useState({});
  const [applying, setApplying] = React.useState(false);

  const setAction = (productId, action, shopifyStock) => {
    setDecisions(prev => ({ ...prev, [productId]: { action, shopify_stock: shopifyStock } }));
  };

  const applyAll = (action) => {
    const d = {};
    data.items.forEach(item => {
      if (!item.in_sync) d[item.product_id] = { action, shopify_stock: item.shopify_stock };
    });
    setDecisions(d);
  };

  const applySync = async () => {
    const list = Object.entries(decisions).map(([product_id, d]) => ({
      product_id, action: d.action, shopify_stock: d.shopify_stock
    }));
    if (!list.length) return;
    setApplying(true);
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/wms/connectors/shopify/apply-sync`, { decisions: list });
      onApplied(r.data);
    } catch (e) {
      onApplied({ error: e.response?.data?.detail || 'Apply failed' });
    }
    setApplying(false);
  };

  const outOfSync = data.items.filter(i => !i.in_sync);
  const decisionCount = Object.keys(decisions).length;

  return (
    <div className="bg-white border rounded-xl overflow-hidden mt-3" data-testid="inventory-diff-table">
      <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold">Inventory Comparison: WMS vs Shopify</p>
          <p className="text-xs text-gray-500">{data.in_sync} in sync · <span className="text-amber-600 font-semibold">{data.out_of_sync} differ</span> · {data.total} total</p>
        </div>
        {outOfSync.length > 0 && (
          <div className="flex gap-2">
            <button onClick={() => applyAll('use_wms')} className="text-[11px] px-3 py-1.5 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-medium">
              All → Use WMS
            </button>
            <button onClick={() => applyAll('use_shopify')} className="text-[11px] px-3 py-1.5 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 font-medium">
              All → Use Shopify
            </button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto max-h-80 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-gray-500">Product</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-500">WMS Stock</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-500">Shopify Stock</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-500">Diff</th>
              <th className="px-3 py-2 text-center font-semibold text-gray-500">Status</th>
              <th className="px-3 py-2 text-center font-semibold text-gray-500">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.items.map(item => {
              const d = decisions[item.product_id];
              return (
                <tr key={item.product_id} className={`${item.in_sync ? '' : 'bg-amber-50/50'}`}>
                  <td className="px-3 py-2">
                    <p className="font-medium">{item.name}</p>
                    <p className="text-[10px] text-gray-400">{item.sku}</p>
                  </td>
                  <td className={`px-3 py-2 text-right font-semibold ${d?.action === 'use_wms' ? 'text-emerald-700 bg-emerald-50 rounded' : ''}`}>{item.wms_stock}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${d?.action === 'use_shopify' ? 'text-amber-700 bg-amber-50 rounded' : ''}`}>{item.shopify_stock ?? '?'}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${item.diff > 0 ? 'text-emerald-600' : item.diff < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                    {item.diff !== null ? (item.diff > 0 ? `+${item.diff}` : item.diff) : '-'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {item.in_sync
                      ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">IN SYNC</span>
                      : <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">DIFFERS</span>}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {!item.in_sync && (
                      <div className="flex gap-1 justify-center">
                        <button onClick={() => setAction(item.product_id, 'use_wms', item.shopify_stock)}
                          className={`text-[10px] px-2 py-1 rounded font-medium ${d?.action === 'use_wms' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-emerald-100'}`}>
                          Use WMS
                        </button>
                        <button onClick={() => setAction(item.product_id, 'use_shopify', item.shopify_stock)}
                          className={`text-[10px] px-2 py-1 rounded font-medium ${d?.action === 'use_shopify' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-amber-100'}`}>
                          Use Shopify
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {decisionCount > 0 && (
        <div className="px-4 py-3 border-t bg-blue-50 flex items-center justify-between">
          <p className="text-xs text-blue-700">{decisionCount} product(s) selected for sync</p>
          <button onClick={applySync} disabled={applying}
            className="text-xs bg-blue-500 text-white px-5 py-2 rounded-lg font-medium hover:bg-blue-600 disabled:opacity-50"
            data-testid="apply-sync-btn">
            {applying ? 'Applying...' : `Apply ${decisionCount} Changes`}
          </button>
        </div>
      )}
    </div>
  );
}
